import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, concatMap, exhaustMap} from 'rxjs/operators';
import {NGXLogger} from 'ngx-logger';
import {of} from 'rxjs';
import {MatSnackBar} from '@angular/material/snack-bar';
import {HttpResponse} from '@angular/common/http';
import {ZahlungsprotokollService} from '../../openapi/zahlung-openapi';
import {ZahlungsprotokollActions} from '../actions/zahlungsprotokoll.actions';


@Injectable()
export class ZahlungsprotokollEffects {

  static readonly DEFAULT_FILENAME = 'Zahlungsprotokoll.pdf';

  constructor(
    private actions$: Actions,
    private zahlungsprotokollService: ZahlungsprotokollService,
    private logger: NGXLogger,
    private snackbar: MatSnackBar,
  ) {
  }

  /**
   * Lädt das Zahlungsprotokoll (PDF/A) zu einem Zahlungslauf vom Zahlung-Service.
   * Der Blob wird in eine base64 Data-URL umgewandelt, damit er serialisierbar im Store liegen kann.
   */
  readonly openZahlungsprotokoll$ = createEffect(
    () => this.actions$
      .pipe(
        ofType(ZahlungsprotokollActions.openZahlungsprotokoll),
        exhaustMap(action => this.zahlungsprotokollService.downloadZahlungsprotokoll(
          action.inhaberId,
          action.zahlungslaufId,
          'response',
        ).pipe(
          concatMap(async (response: HttpResponse<Blob>) => {
            const blob = response.body as Blob;
            const filename = ZahlungsprotokollEffects.extractFilename(response.headers.get('content-disposition'));
            const content = await ZahlungsprotokollEffects.toDataUrl(blob);

            this.logger.debug('load zahlungsprotokoll succeeded. zahlungslaufId:', action.zahlungslaufId);

            return ZahlungsprotokollActions.loadZahlungsprotokollSuccess({
              zahlungslaufId: action.zahlungslaufId,
              content,
              filename,
            });
          }),
          catchError(error => {
            this.logger.error('Could not load zahlungsprotokoll', error);

            let errorMsg: string;
            switch (error?.status) {
              case 404: {
                errorMsg = 'Zahlungsprotokoll wurde nicht gefunden';
                break;
              }
              case 422: {
                errorMsg = 'Für diese Zahlung liegt kein Zahlungsprotokoll vor';
                break;
              }
              default: {
                errorMsg = 'Fehler beim Laden des Zahlungsprotokolls';
              }
            }
            this.snackbar.open(errorMsg, undefined, {duration: 5000, panelClass: 'error'});

            return of(ZahlungsprotokollActions.loadZahlungsprotokollFailure({status: error?.status}));
          }),
        )),
      )
  );

  /**
   * Liest den Dateinamen aus dem Content-Disposition Header (z.B. `attachment; filename="Zahlungsprotokoll_2026-10-15_5.pdf"`).
   */
  static extractFilename(contentDisposition: string | null): string {
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition || '');
    const filename = match?.[1]?.trim();
    return filename ? decodeURIComponent(filename) : ZahlungsprotokollEffects.DEFAULT_FILENAME;
  }

  static toDataUrl(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  }
}
